import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { CreditCard, AlertCircle, RefreshCw, Check } from 'lucide-react';

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/billing');
      setData(r.data);
    } catch (e) {
      console.error('[admin/billing] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};

  return (
    <AdminLayout title="Billing" description="Future-ready billing state. Payments are NOT live yet — Stripe will plug in here when ready.">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-400 font-mono">{loading ? 'Loading…' : 'Subscription counts'}</p>
        <button onClick={load} data-testid="admin-billing-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Processor status */}
      <div className="border gold-border bg-gradient-to-r from-[#13110a] to-[#0f0d08] rounded-md p-5 flex items-start gap-3 mb-8" data-testid="admin-billing-status">
        <AlertCircle size={18} className="gold-text mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-white">Payment processor not connected yet</p>
          <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
            {data?.message || 'Stripe integration will plug into the fields below when ready. All users default to "Beta Free Access" until then.'}
          </p>
        </div>
      </div>

      {/* User count by plan status */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-px w-6 bg-[#d8b34a]" />
          <p className="text-xs tracking-[0.4em] font-mono gold-text">PLAN BREAKDOWN</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-billing-counts">
          <CountTile label="Beta" value={counts.beta} tone="accent" />
          <CountTile label="Free" value={counts.free} />
          <CountTile label="Paid" value={counts.paid} tone="ok" />
          <CountTile label="Canceled" value={counts.canceled} tone="warn" />
          <CountTile label="Suspended" value={counts.suspended} tone="bad" />
        </div>
      </section>

      {/* Planned fields */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <span className="h-px w-6 bg-[#d8b34a]" />
          <p className="text-xs tracking-[0.4em] font-mono gold-text">DATABASE READINESS</p>
        </div>
        <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={14} className="gold-text" />
            <p className="text-sm font-medium text-white">User-record fields prepared for Stripe</p>
          </div>
          <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
            These columns are already initialized on every user record. When Stripe is wired up, the webhook handler fills them in — no schema migration needed.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {(data?.planned_fields || []).map((f) => (
              <li key={f} className="flex items-center gap-2 py-2 px-3 border border-white/5 rounded-sm bg-white/[0.02]" data-testid={`billing-field-${f}`}>
                <Check size={12} className="gold-text" />
                <code className="text-xs font-mono text-zinc-200">{f}</code>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <p className="mt-10 text-center text-xs text-zinc-500">
        Use <code className="text-zinc-400">PATCH /api/admin/users/:user_id/status</code> from the Users page to manually move accounts between beta · free · paid · canceled · suspended.
      </p>
    </AdminLayout>
  );
}

function CountTile({ label, value, tone }) {
  const tones = {
    accent: 'border-[#d8b34a]/40 bg-[#13110a]',
    ok: 'border-emerald-500/40',
    warn: 'border-amber-500/40',
    bad: 'border-red-500/40',
  };
  return (
    <div className={`border rounded-md p-5 bg-gradient-to-br from-[#0e0e10] to-[#0a0a0c] ${tones[tone] || 'border-white/10'}`} data-testid={`billing-count-${label.toLowerCase()}`}>
      <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">{label.toUpperCase()}</p>
      <p className="mt-3 text-3xl font-display font-bold text-white tabular-nums">{value ?? '—'}</p>
    </div>
  );
}
